import React from 'react';
import styled from 'styled-components';
import { Spin } from 'antd';
import { Switch, Redirect } from 'react-router-dom';
import loadable from '@loadable/component';
import CommonRoute from './CommonRoute';
import AuthRoute from './AuthRoute';

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loading = () => (
  <Wrapper>
    <Spin spinning />
  </Wrapper>
);

// Login
const Login = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "login" */ 'pages/Login'
    ),
  { fallback: <Loading /> }
);

// Patients

const PatientsList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "patients-list" */ 'pages/Patients/List'
    ),
  { fallback: <Loading /> }
);

const CreatePatient = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-patient" */ 'pages/Patients/Create'
    ),
  { fallback: <Loading /> }
);

const EditPatient = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-patient" */ 'pages/Patients/Edit'
    ),
  { fallback: <Loading /> }
);

const PatientChangePassword = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "patient-change-password" */ 'pages/Patients/ChangePassword'
    ),
  { fallback: <Loading /> }
);

const PatientDetail = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "patient-detail" */ 'pages/Patients/Detail'
    ),
  { fallback: <Loading /> }
);

const CreatePatientProgram = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-patient-program" */ 'pages/Patients/Program/Create'
    ),
  { fallback: <Loading /> }
);

const EditPatientProgram = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-patient-program" */ 'pages/Patients/Program/Edit'
    ),
  { fallback: <Loading /> }
);

const PatientProgramDetail = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "patient-program-detail" */ 'pages/Patients/Program/Detail'
    ),
  { fallback: <Loading /> }
);

const CreateProgramMeasurement = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-program-measurement" */ 'pages/Patients/Program/Measurement/Create'
    ),
  { fallback: <Loading /> }
);

const EditProgramMeasurement = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-program-measurement" */ 'pages/Patients/Program/Measurement/Edit'
    ),
  { fallback: <Loading /> }
);

const CreateProgramPrescription = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-program-prescription" */ 'pages/Patients/Program/Prescription/Create'
    ),
  { fallback: <Loading /> }
);

const EditProgramPrescription = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-program-prescription" */ 'pages/Patients/Program/Prescription/Edit'
    ),
  { fallback: <Loading /> }
);

const ProgramIndexEvalDetail = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "program-index-eval-detail" */ 'pages/Patients/Program/IndexEval/Detail'
    ),
  { fallback: <Loading /> }
);

const CreateProgramIndexEval = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-program-index-eval" */ 'pages/Patients/Program/IndexEval/Create'
    ),
  { fallback: <Loading /> }
);

const EditProgramIndexEval = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-program-index-eval" */ 'pages/Patients/Program/IndexEval/Edit'
    ),
  { fallback: <Loading /> }
);

// FeedbackForm

const FeedbackFormList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "feedback-form-list" */ 'pages/FeedbackForm/List'
    ),
  { fallback: <Loading /> }
);

const CreateFeedbackForm = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-feedback-form" */ 'pages/FeedbackForm/Create'
    ),
  { fallback: <Loading /> }
);

const EditFeedbackForm = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-feedback-form" */ 'pages/FeedbackForm/Edit'
    ),
  { fallback: <Loading /> }
);

// Staff

const StaffList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "staff-list" */ 'pages/Staff/List'
    ),
  { fallback: <Loading /> }
);

const CreateStaff = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-staff" */ 'pages/Staff/Create'
    ),
  { fallback: <Loading /> }
);

const EditStaff = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-staff" */ 'pages/Staff/Edit'
    ),
  { fallback: <Loading /> }
);

const ChangePassword = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "change-password" */ 'pages/Staff/ChangePassword'
    ),
  { fallback: <Loading /> }
);

// ExerciseCategory

const ExerciseCategoryList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "exercise-category-list" */ 'pages/ExerciseCategory/List'
    ),
  { fallback: <Loading /> }
);

const CreateExerciseCategory = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-exercise-category" */ 'pages/ExerciseCategory/Create'
    ),
  { fallback: <Loading /> }
);

const EditExerciseCategory = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-exercise-category" */ 'pages/ExerciseCategory/Edit'
    ),
  { fallback: <Loading /> }
);

// Exercise

const ExerciseList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "exercise-list" */ 'pages/Exercise/List'
    ),
  { fallback: <Loading /> }
);

const CreateExercise = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-exercise" */ 'pages/Exercise/Create'
    ),
  { fallback: <Loading /> }
);

const EditExercise = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-exercise" */ 'pages/Exercise/Edit'
    ),
  { fallback: <Loading /> }
);

// IMUPosition

const IMUPositionList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "imu-position-list" */ 'pages/IMUPosition/List'
    ),
  { fallback: <Loading /> }
);

const CreateIMUPosition = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-imu-position" */ 'pages/IMUPosition/Create'
    ),
  { fallback: <Loading /> }
);

const EditIMUPosition = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-imu-position" */ 'pages/IMUPosition/Edit'
    ),
  { fallback: <Loading /> }
);

// PrescriptionTemplate

const PrescriptionTemplateList = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "prescription-template-list" */ 'pages/PrescriptionTemplate/List'
    ),
  { fallback: <Loading /> }
);

const CreatePrescriptionTemplate = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "create-prescription-template" */ 'pages/PrescriptionTemplate/Create'
    ),
  { fallback: <Loading /> }
);

const EditPrescriptionTemplate = loadable(
  () =>
    import(
      /*  webpackPrefetch: true, webpackChunkName: "edit-prescription-template" */ 'pages/PrescriptionTemplate/Edit'
    ),
  { fallback: <Loading /> }
);

export default () => (
  <Switch>
    <CommonRoute key="login" exact path="/" component={Login} />
    <AuthRoute key="patients" exact path="/patients" component={PatientsList} />
    <AuthRoute
      key="patients"
      exact
      path="/patients/create"
      component={CreatePatient}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/edit"
      component={EditPatient}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/change-password"
      component={PatientChangePassword}
    />
    <AuthRoute
      key="patients"
      exact
      path={[
        '/patients/:id/program/:id/measurement',
        '/patients/:id/program/:id/prescription',
        '/patients/:id/program/:id/stats',
        '/patients/:id/program/:id/index-eval',
      ]}
      component={PatientProgramDetail}
    />
    <AuthRoute
      key="patients"
      exact
      path={['/patients/:id/info', '/patients/:id/program']}
      component={PatientDetail}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/create"
      component={CreatePatientProgram}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/edit"
      component={EditPatientProgram}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/measurement/create"
      component={CreateProgramMeasurement}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/measurement/:id/edit"
      component={EditProgramMeasurement}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/prescription/create"
      component={CreateProgramPrescription}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/prescription/:id/edit"
      component={EditProgramPrescription}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/index-eval/create"
      component={CreateProgramIndexEval}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/index-eval/:id/edit"
      component={EditProgramIndexEval}
    />
    <AuthRoute
      key="patients"
      exact
      path="/patients/:id/program/:id/index-eval/:id"
      component={ProgramIndexEvalDetail}
    />
    <Redirect from="/patients/:id" to="/patients/:id/info" />
    <AuthRoute
      key="feedback-form"
      exact
      path="/feedback-form"
      component={FeedbackFormList}
    />
    <AuthRoute
      key="feedback-form"
      exact
      path="/feedback-form/create"
      component={CreateFeedbackForm}
    />
    <AuthRoute
      key="feedback-form"
      exact
      path="/feedback-form/:id/edit"
      component={EditFeedbackForm}
    />
    <AuthRoute key="staff" exact path="/staff" component={StaffList} />
    <AuthRoute key="staff" exact path="/staff/create" component={CreateStaff} />
    <AuthRoute key="staff" exact path="/staff/edit" component={EditStaff} />
    <AuthRoute
      key="staff"
      exact
      path="/staff/change-password"
      component={ChangePassword}
    />
    <AuthRoute
      key="exercise-category"
      exact
      path="/exercise-category"
      component={ExerciseCategoryList}
    />
    <AuthRoute
      key="exercise-category"
      exact
      path="/exercise-category/create"
      component={CreateExerciseCategory}
    />
    <AuthRoute
      key="exercise-category"
      exact
      path="/exercise-category/:id/edit"
      component={EditExerciseCategory}
    />
    <AuthRoute key="exercise" exact path="/exercise" component={ExerciseList} />
    <AuthRoute
      key="exercise"
      exact
      path="/exercise/create"
      component={CreateExercise}
    />
    <AuthRoute
      key="exercise"
      exact
      path="/exercise/:id/edit"
      component={EditExercise}
    />
    <AuthRoute
      key="imu-position"
      exact
      path="/imu-position"
      component={IMUPositionList}
    />
    <AuthRoute
      key="imu-position"
      exact
      path="/imu-position/create"
      component={CreateIMUPosition}
    />
    <AuthRoute
      key="imu-position"
      exact
      path="/imu-position/:id/edit"
      component={EditIMUPosition}
    />
    <AuthRoute
      key="prescription-template"
      exact
      path="/prescription-template"
      component={PrescriptionTemplateList}
    />
    <AuthRoute
      key="prescription-template"
      exact
      path="/prescription-template/create"
      component={CreatePrescriptionTemplate}
    />
    <AuthRoute
      key="prescription-template"
      exact
      path="/prescription-template/:id/edit"
      component={EditPrescriptionTemplate}
    />
    <Redirect to="/" />
  </Switch>
);
